export const theme = {
  // Common theme properties
  common: {
    dark: {
      primary: 'orange',
      text: {
        primary: 'text-gray-300',
        accent: 'text-orange-500',
        hover: 'hover:text-orange-500',
      },
      border: {
        primary: 'border-orange-500',
        gradient: 'from-orange-400 via-orange-600 to-yellow-400',
      },
      background: {
        primary: 'bg-orange-600',
        hover: 'hover:bg-orange-700',
        glow: 'bg-orange-500/20',
        glass: 'bg-gradient-to-r from-orange-900/60 to-orange-700/40 backdrop-blur-md',
      },
      shadow: {
        primary: 'shadow-orange-500',
        glow: 'shadow-[0_0_60px_10px_rgba(255,140,0,0.25)]',
      },
      ring: {
        glow: 'ring-orange-400/40',
      },
    },
    light: {
      primary: 'black',
      text: {
        primary: 'text-gray-700',
        accent: 'text-black',
        hover: 'hover:text-black',
      },
      border: {
        primary: 'border-black',
        gradient: 'from-black via-black to-black',
      },
      background: {
        primary: 'bg-black',
        hover: 'hover:bg-gray-900',
        glow: 'bg-black/10',
        glass: 'bg-white/60 backdrop-blur-xl border border-white/40',
      },
      shadow: {
        primary: 'shadow-black',
        glow: 'shadow-[0_0_60px_10px_rgba(0,0,0,0.18)]',
      },
      ring: {
        glow: 'ring-black/80',
      },
    },
  },

  // Hero component specific theme
  hero: {
    dark: {
      badge: {
        text: 'text-orange-100',
        subText: 'text-orange-200',
      },
      description: {
        background: 'bg-orange-950/60 border-orange-700 text-orange-100',
      },
      code: {
        const: 'text-orange-400',
        var: 'text-orange-300',
        equals: 'text-orange-400',
        brace: 'text-orange-200',
        string: 'text-orange-200',
        value: 'text-orange-200',
      },
      social: {
        color: 'text-orange-400',
      },
      button: {
        outline: 'border-orange-500 text-orange-500 hover:bg-orange-600 hover:text-white',
      },
    },
    light: {
      badge: {
        text: 'text-teal-700',
        subText: 'text-amber-700',
      },
      description: {
        background: 'bg-white/80 border-gray-200 text-gray-800 backdrop-blur-sm',
      },
      code: {
        const: 'text-teal-700 font-bold',
        var: 'text-amber-700 font-semibold',
        equals: 'text-teal-700 font-bold',
        brace: 'text-zinc-500',
        string: 'text-sky-700',
        value: 'text-sky-700',
      },
      social: {
        color: 'text-gray-700',
      },
      button: {
        outline: 'border-black text-black hover:bg-black hover:text-white',
      },
    },
  },

  // About component specific theme
  about: {
    dark: {
      background: 'bg-black/50',
      text: {
        main: 'text-neutral-100',
        accent: 'text-orange-400',
      },
      ring: {
        accent: 'ring-orange-400',
      },
      card: {
        background: 'bg-black/95',
        gradient: 'from-orange-500/10 to-teal-500/10',
        radialGradient: 'rgba(249,115,22,0.15)',
        shadow: '0 8px 40px 0 rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1), 0 0 20px rgba(249,115,22,0.2)',
      },
      skills: {
        border: 'border-orange-400/40',
        text: 'text-orange-200',
        hover: {
          background: 'hover:bg-orange-400/20',
          shadow: 'hover:shadow-[0_0_15px_rgba(249,115,22,0.4)]',
        },
      },
      info: {
        icon: {
          background: 'bg-orange-400/20',
        },
        text: {
          accent: 'text-orange-200/90',
        },
      },
    },
    light: {
      background: 'bg-white/50',
      text: {
        main: 'text-neutral-900',
        accent: 'text-teal-600',
      },
      ring: {
        accent: 'ring-teal-500',
      },
      card: {
        background: 'bg-white/90',
        gradient: 'from-orange-500/5 to-teal-500/5',
        radialGradient: 'rgba(249,115,22,0.1)',
        shadow: '0 8px 40px 0 rgba(16,16,16,0.10)',
      },
      skills: {
        border: 'border-teal-400/30',
        text: 'text-teal-700',
        hover: {
          background: 'hover:bg-teal-400/10',
          shadow: 'hover:shadow-[0_0_15px_rgba(20,184,166,0.3)]',
        },
      },
      info: {
        icon: {
          background: 'bg-teal-400/10',
        },
        text: {
          accent: 'text-gray-600',
        },
      },
    },
  },

  experience: {
    dark: {
      background: 'bg-black/95',
      text: {
        main: 'text-gray-300',
        accent: 'text-orange-400',
        title: 'text-white',
        period: 'text-gray-400',
        badge: 'text-orange-400',
        badgeBg: 'bg-black/80',
        badgeBorder: 'border-orange-400/30'
      },
      card: {
        background: 'bg-black/80',
        border: 'border-orange-400/30',
        shadow: 'shadow-orange-500/20',
        hover: {
          shadow: '0 0 32px 4px rgba(249,115,22,0.2), 0 0 0 2px rgba(249,115,22,0.3)',
          background: 'linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(20,184,166,0.15) 100%)'
        }
      },
      freelance: {
        border: 'border-orange-400/40',
        background: 'bg-black/90',
        gradient: 'linear-gradient(135deg, rgba(249,115,22,0.12) 0%, rgba(20,184,166,0.12) 100%)',
        hover: {
          gradient: 'linear-gradient(135deg, rgba(249,115,22,0.22) 0%, rgba(20,184,166,0.22) 100%)',
          shadow: '0 0 80px 24px rgba(249,115,22,0.32), 0 0 0 8px rgba(249,115,22,0.38)'
        }
      }
    },
    light: {
      background: 'bg-gray-50/95',
      text: {
        main: 'text-gray-700',
        accent: 'text-teal-700',
        title: 'text-gray-900',
        period: 'text-teal-700',
        badge: 'text-teal-700',
        badgeBg: 'bg-gray-100',
        badgeBorder: 'border-teal-400/30'
      },
      card: {
        background: 'bg-white/80',
        border: 'border-teal-400/30',
        shadow: 'shadow-teal-500/20',
        hover: {
          shadow: '0 0 32px 4px rgba(20,184,166,0.15), 0 0 0 2px rgba(20,184,166,0.25)',
          background: 'linear-gradient(135deg, rgba(20,184,166,0.12) 0%, rgba(249,115,22,0.12) 100%)'
        }
      },
      freelance: {
        border: 'border-teal-400/40',
        background: 'bg-white/90',
        gradient: 'linear-gradient(135deg, rgba(20,184,166,0.08) 0%, rgba(249,115,22,0.08) 100%)',
        hover: {
          gradient: 'linear-gradient(135deg, rgba(20,184,166,0.18) 0%, rgba(249,115,22,0.18) 100%)',
          shadow: '0 0 80px 24px rgba(20,184,166,0.22), 0 0 0 8px rgba(20,184,166,0.28)'
        }
      }
    }
  },

  skills: {
    dark: {
      background: 'bg-black/95',
      text: {
        main: 'text-orange-100',
        accent: 'text-orange-500',
        hover: 'group-hover:text-black'
      },
      card: {
        background: 'bg-gradient-to-r from-orange-900/60 to-orange-700/40 backdrop-blur-md',
        hover: 'hover:bg-gradient-to-r hover:from-orange-800/80 hover:to-orange-600/60',
        border: 'border-orange-400/30',
        shadow: 'shadow-[0_0_60px_10px_rgba(255,140,0,0.15)]',
        hoverShadow: 'hover:shadow-[0_0_60px_10px_rgba(255,140,0,0.25)]',
        gradient: 'from-orange-400 via-orange-600 to-yellow-400'
      },
      icon: {
        color: '#f97316',
        hoverColor: '#000000',
        shadow: 'drop-shadow-[0_0_12px_rgba(249,115,22,0.4)]'
      }
    },
    light: {
      background: 'bg-gray-50/95',
      text: {
        main: 'text-gray-800',
        accent: 'text-teal-600',
        hover: 'group-hover:text-white'
      },
      card: {
        background: 'bg-white/90 backdrop-blur-xl border border-gray-100/40',
        hover: 'hover:bg-gradient-to-r hover:from-teal-600/95 hover:to-teal-500/95',
        border: 'border-teal-400/30',
        shadow: 'shadow-[0_0_60px_10px_rgba(0,0,0,0.08)]',
        hoverShadow: 'hover:shadow-[0_0_60px_10px_rgba(0,0,0,0.12)]',
        gradient: 'from-teal-500 via-teal-600 to-teal-400'
      },
      icon: {
        color: '#0d9488',
        hoverColor: '#ffffff',
        shadow: 'drop-shadow-[0_0_12px_rgba(13,148,136,0.3)]'
      }
    }
  },

  projects: {
    dark: {
      background: 'bg-black/95',
      text: {
        main: 'text-orange-100',
        accent: 'text-orange-500',
        description: 'text-gray-300',
        tech: 'text-gray-200',
        feature: 'text-gray-300'
      },
      card: {
        background: 'bg-zinc-900/95',
        hover: 'hover:bg-zinc-800/95',
        border: 'border-orange-500/30',
        shadow: 'shadow-[0_0_30px_rgba(249,115,22,0.15)]',
        hoverShadow: 'shadow-[0_0_40px_rgba(249,115,22,0.2)]'
      },
      button: {
        outline: 'border-orange-500 text-orange-500 hover:bg-orange-500/10',
        primary: 'bg-orange-500 hover:bg-orange-600 text-white'
      },
      tech: {
        background: 'bg-zinc-800/80',
        border: 'border-zinc-700',
        text: 'text-zinc-200',
        hover: {
          background: 'hover:bg-orange-500/20',
          border: 'hover:border-orange-500'
        }
      }
    },
    light: {
      background: 'bg-gray-50/95',
      text: {
        main: 'text-gray-900',
        accent: 'text-teal-600',
        description: 'text-gray-600',
        tech: 'text-gray-700',
        feature: 'text-gray-600'
      },
      card: {
        background: 'bg-white/90',
        hover: 'hover:bg-gray-50/90',
        border: 'border-gray-200/50',
        shadow: 'shadow-[0_0_30px_rgba(20,184,166,0.15)]',
        hoverShadow: 'shadow-[0_0_40px_rgba(20,184,166,0.2)]'
      },
      button: {
        outline: 'border-teal-500 text-teal-500 hover:bg-teal-500/10',
        primary: 'bg-teal-500 hover:bg-teal-600 text-white'
      },
      tech: {
        background: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-700',
        hover: {
          background: 'hover:bg-teal-500/20',
          border: 'hover:border-teal-500'
        }
      }
    }
  },

  services: {
    dark: {
      background: 'bg-black/95',
      text: {
        main: 'text-orange-100',
        accent: 'text-orange-500',
        description: 'text-gray-300',
        heading: 'text-orange-100'
      },
      card: {
        background: 'bg-black/40',
        border: 'border-orange-500/20',
        hover: {
          border: 'hover:border-orange-500/50',
          shadow: 'hover:shadow-orange-500/20'
        }
      },
      icon: {
        background: 'bg-orange-500/20',
        color: 'text-orange-500',
        hover: 'hover:bg-orange-500/20'
      },
      line: {
        background: 'bg-orange-500/20'
      },
      gradient: {
        primary: 'from-orange-500/5',
        secondary: 'from-orange-500/10'
      }
    },
    light: {
      background: 'bg-gray-50/95',
      text: {
        main: 'text-gray-800',
        accent: 'text-teal-600',
        description: 'text-gray-600',
        heading: 'text-gray-800'
      },
      card: {
        background: 'bg-white/40',
        border: 'border-teal-500/20',
        hover: {
          border: 'hover:border-teal-500/50',
          shadow: 'hover:shadow-teal-500/20'
        }
      },
      icon: {
        background: 'bg-teal-500/20',
        color: 'text-teal-500',
        hover: 'hover:bg-teal-500/20'
      },
      line: {
        background: 'bg-teal-500/20'
      },
      gradient: {
        primary: 'from-teal-500/5',
        secondary: 'from-teal-500/10'
      }
    }
  },

  contact: {
    dark: {
      background: 'bg-black/95',
      text: {
        main: 'text-white',
        secondary: 'text-gray-300',
        heading: 'text-orange-100',
        code: 'text-orange-400'
      },
      card: {
        background: 'bg-gray-900/50',
        border: 'border-gray-800',
        hover: {
          border: 'hover:border-orange-500/50',
          shadow: 'hover:shadow-[0_0_20px_rgba(249,115,22,0.2)]'
        }
      },
      input: {
        background: 'bg-black/50',
        border: 'border-gray-800',
        hover: 'hover:border-orange-500/50'
      },
      icon: {
        background: 'bg-orange-500/10',
        color: 'text-orange-500',
        hover: 'hover:bg-orange-500/20'
      },
      button: {
        primary: 'from-orange-500 to-orange-600',
        hover: 'from-orange-600 to-orange-700',
        text: 'text-white',
        hoverText: 'hover:text-orange-100'
      },
      link: {
        color: 'text-orange-400',
        hover: 'hover:text-orange-300'
      }
    },
    light: {
      background: 'bg-gray-50/95',
      text: {
        main: 'text-gray-900',
        secondary: 'text-gray-600',
        heading: 'text-gray-800',
        code: 'text-teal-700'
      },
      card: {
        background: 'bg-white/90',
        border: 'border-gray-200',
        hover: {
          border: 'hover:border-teal-500/50',
          shadow: 'hover:shadow-[0_0_20px_rgba(20,184,166,0.2)]'
        }
      },
      input: {
        background: 'bg-gray-50',
        border: 'border-gray-200',
        hover: 'hover:border-teal-500/50'
      },
      icon: {
        background: 'bg-teal-500/10',
        color: 'text-teal-600',
        hover: 'hover:bg-teal-500/20'
      },
      button: {
        primary: 'from-teal-500 to-teal-600',
        hover: 'from-teal-600 to-teal-700',
        text: 'text-white',
        hoverText: 'hover:text-teal-50'
      },
      link: {
        color: 'text-teal-700',
        hover: 'hover:text-teal-800'
      }
    }
  },

  navigation: {
    dark: {
      background: 'bg-black/70 border-b border-orange-700 shadow-lg backdrop-blur-2xl',
      text: {
        main: 'text-orange-100',
        accent: 'text-orange-500',
        hover: 'hover:text-orange-400'
      },
      logo: {
        glow: 'radial-gradient(circle, rgba(249,115,22,0.35) 0%, rgba(0,0,0,0.0) 70%)',
        shadow: 'drop-shadow-[0_0_8px_rgba(249,115,22,0.5)]'
      },
      mobile: {
        border: '1px solid rgba(249,115,22,0.2)'
      }
    },
    light: {
      background: 'bg-white/60 border-b border-black/10 shadow-md backdrop-blur-2xl',
      text: {
        main: 'text-black',
        accent: 'text-teal-700',
        hover: 'hover:text-teal-700'
      },
      logo: {
        glow: '',
        shadow: ''
      },
      mobile: {
        border: '1px solid rgba(0,0,0,0.1)'
      }
    }
  }
}; 